import type Currency from '../../../../types/payment/Currency';
import formatPriceWithCurrency from './utils/formatPriceWithCurrency';
import useTranslation, { type TextKeyCode } from 'canvas_states/translation/useTranslation';
import useAppConfig from 'canvas_states/config/useAppConfig';
import useCurrentLanguage from 'canvas_states/language/useCurrentLanguage';
import { NO_BREAK_SPACE } from './utils/unbreakableSpaces';
import { useCallback } from 'react';
import mapLanguageCodeForStringFormat from '../time/utils/mapLanguageCodeForStringFormat';

/**
 * @deprecated
 * This hook should never be used directly.
 * This will only work for points right now. use useFormatCurrency for other currencies instead
 */
const useNativeFormatCurrency = () => {
  const { t } = useTranslation().actions;
  const { config } = useAppConfig().states;
  const { currentLanguage } = useCurrentLanguage().states;
  const currencyConfig = config.currencyFormatSettings;

  const addCurrencySymbol = useCallback(
    (formattedAmount: string, currency: Currency) =>
      formatPriceWithCurrency(
        formattedAmount,
        currencyConfig,
        currency.symbol,
        t(currency.label as TextKeyCode),
        currentLanguage.code,
      ),
    [currencyConfig, t, currentLanguage.code],
  );

  const formatAmount = useCallback(
    (amount: number, options?: Intl.NumberFormatOptions) => {
      const formatter = new Intl.NumberFormat(mapLanguageCodeForStringFormat(currentLanguage.code), options);
      return formatter.format(amount).replace(/\u202F/g, NO_BREAK_SPACE);
    },
    [currentLanguage.code],
  );

  const actions = {
    formatAmountWithoutCurrency: (amount: number, currency: Currency) => {
      if (Math.floor(amount) !== amount) {
        return actions.formatAmountWithoutCurrencyForceDigits(amount, currency);
      }

      return formatAmount(amount);
    },
    formatAmountWithCurrency: (amount: number, currency: Currency) => {
      const formattedAmount = actions.formatAmountWithoutCurrency(amount, currency);
      return addCurrencySymbol(formattedAmount, currency);
    },
    // eslint-disable-next-line no-unused-vars, unused-imports/no-unused-vars
    formatAmountWithoutCurrencyForceDigits: (amount: number, _currency: Currency) =>
      formatAmount(
        amount,
        currencyConfig
          ? {
              minimumFractionDigits: currencyConfig.fractionDigits,
              maximumFractionDigits: currencyConfig.fractionDigits,
            }
          : {},
      ),
    formatAmountWithCurrencyForceDigits: (amount: number, currency: Currency) => {
      const formattedAmount = actions.formatAmountWithoutCurrencyForceDigits(amount, currency);
      return addCurrencySymbol(formattedAmount, currency);
    },
  };

  return {
    actions,
  };
};

export default useNativeFormatCurrency;
